import { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { dayRange } from "@/lib/tina/festivals";
import { ConfirmDialog } from "@/pages/tina/festivals/components/confirm-dialog";
import {
  FESTIVAL_QUERY_ROOT,
  useFestivalRepository,
} from "@/pages/tina/festivals/hooks/use-festival-repository";
import {
  DIRTY_DROPPED,
  mergeCandidates,
} from "@/pages/tina/festivals/lib/page-copy";
import type { FestivalPageRead } from "@/types/tina-festival-page";

interface MergeDialogProps {
  page: FestivalPageRead;
  dirty: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMerge: (intoRecordId: string) => void;
}

/** Fold this festival into another of the same city. Tina judges the merge; the list is only a convenience. */
export function MergeDialog({
  page,
  dirty,
  open,
  onOpenChange,
  onMerge,
}: MergeDialogProps) {
  const repo = useFestivalRepository();
  const [into, setInto] = useState<string | null>(null);
  const query = useQuery({
    queryKey: [FESTIVAL_QUERY_ROOT, "merge-candidates"],
    queryFn: () => repo.fetchMergeCandidates(),
    enabled: open,
    staleTime: 0,
  });
  useEffect(() => {
    if (!open) setInto(null);
  }, [open]);

  const candidates = mergeCandidates(query.data ?? [], page);
  const target = candidates.find((row) => row.record_id === into);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Merge ${page.name} into another festival?`}
      description={
        target
          ? `${page.name} joins ${target.name}: its pages, names and evidence move over and ${page.name} is closed. This cannot be undone here.${dirty ? ` ${DIRTY_DROPPED}` : ""}`
          : "Pick the festival this one is part of."
      }
      confirmLabel="Merge festivals"
      cancelLabel="Keep them apart"
      confirmDisabled={target === undefined}
      onConfirm={() => {
        if (target) onMerge(target.record_id);
      }}
    >
      {query.isSuccess && candidates.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No other festival in {page.city ?? "this city"}.
        </p>
      ) : (
        <Select value={into ?? undefined} onValueChange={setInto}>
          <SelectTrigger aria-label="Festival to merge into">
            <SelectValue placeholder="Choose a festival" />
          </SelectTrigger>
          <SelectContent>
            {candidates.map((row) => (
              <SelectItem key={row.record_id} value={row.record_id}>
                {row.name} · {dayRange(row.first_day, row.last_day)} ·{" "}
                {row.status_label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </ConfirmDialog>
  );
}
